import type { ShpeshftDocument, VectorObject } from './document';
import type { BooleanOperation } from '../engine/boolean';

export type BooleanTransaction = Readonly<{
  token: number;
  operation: BooleanOperation;
  document: ShpeshftDocument;
  sourceIds: readonly string[];
  sourceObjects: readonly VectorObject[];
  sourceOrder: readonly string[];
  insertionIndex: number;
}>;

export function captureBooleanTransaction(document: ShpeshftDocument, selectedIds: readonly string[], operation: BooleanOperation, token: number): BooleanTransaction {
  const sourceIds = [...selectedIds];
  return {
    token,
    operation,
    document,
    sourceIds,
    sourceObjects: sourceIds.map((id) => document.objects[id]).filter((object): object is VectorObject => Boolean(object)),
    sourceOrder: [...document.order],
    insertionIndex: Math.min(...sourceIds.map((id) => document.order.indexOf(id)))
  };
}

export function booleanTransactionIsCurrent(transaction: BooleanTransaction, document: ShpeshftDocument, currentToken: number): boolean {
  return transaction.token === currentToken
    && transaction.document === document
    && transaction.sourceIds.every((id) => document.objects[id] === transaction.document.objects[id]);
}

export function shouldUndoTwoFingerTap(gesture: { moved: boolean; started: number; suppressUndo: boolean }, now: number): boolean {
  return !gesture.suppressUndo && !gesture.moved && now - gesture.started < 500;
}

export function shouldIgnoreAdditionalPointer(activeDirectManipulation:boolean,existingPointerCount:number):boolean{return activeDirectManipulation&&existingPointerCount>0;}
export function isActiveDirectManipulation(dragMoved:boolean|undefined,nodeMoved:boolean|undefined):boolean{return dragMoved===true||nodeMoved===true;}
export function shouldStartIdlePinch(activeDirectManipulation:boolean,pointerCountAfterDown:number):boolean{return !activeDirectManipulation&&pointerCountAfterDown===2;}
export function selectionAfterTap(currentIds:readonly string[],hitIds:readonly string[],additive:boolean):string[]{if(!additive)return[...hitIds];const remove=hitIds.every((id)=>currentIds.includes(id));return remove?currentIds.filter((id)=>!hitIds.includes(id)):[...new Set([...currentIds,...hitIds])];}
export function shouldExitCornerMode(corner:'topLeft'|'topRight'|'bottomRight'|'bottomLeft',moved:boolean|undefined):boolean{return Boolean(corner)&&moved!==true;}

export function selectionControlAvailability(selectionCount:number,committedGroup=false){const normalObject=selectionCount===1||committedGroup;return{dimension:normalObject,nodeEdit:normalObject,actions:selectionCount>0,scale:selectionCount>0,rotate:selectionCount>0};}

export function restoreObjectSnapshots(document: ShpeshftDocument, snapshots: Readonly<Record<string, VectorObject>>): ShpeshftDocument {
  return { ...document, objects: { ...document.objects, ...snapshots } };
}

export function segmentSelectionIsValid(document: ShpeshftDocument, objectId: string | null, segment: { pathIndex:number; index:number } | null): boolean {
  if (!objectId || !segment) return false;
  const object = document.objects[objectId];
  if (!object || object.geometry.kind !== 'path') return false;
  const paths = [object.geometry.nodes, ...(object.geometry.subpaths ?? [])];
  const nodes = paths[segment.pathIndex];
  if (!nodes) return false;
  const segmentCount = segment.pathIndex === 0 && !object.geometry.closed ? Math.max(0, nodes.length - 1) : nodes.length;
  return segment.index >= 0 && segment.index < segmentCount;
}
